import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { RichText } from '../../components/RichText';
import { RichTextEditor } from '../../components/RichTextEditor';
import { Select } from '../../components/Select';
import { SkeletonTable } from '../../components/Skeleton';
import type { EmailTemplate, MailEvent } from './api';
import {
  useAssignMailEvent,
  useCreateEmailTemplate,
  useDeleteEmailTemplate,
  useEmailTemplates,
  useMailEvents,
  usePreviewEmailTemplate,
  useUpdateEmailTemplate,
} from './queries';

/**
 * Mailvorlagen und Anlaesse -- F3-21.
 *
 * **Die zentrale Stelle fuer jede Mail.** Oben die Anlaesse: jede Mail, die
 * das System verschickt, mit der Vorlage, die sie benutzt, oder "Nicht
 * versenden". Darunter die Vorlagen selbst, und der Editor mit den Variablen
 * des gewaehlten Anlasses und einer Vorschau.
 *
 * **Die Variablen kommen vom Server**, je Anlass. Die Oberflaeche kennt keine
 * eigene Liste -- sie liefe beim naechsten Anlass auseinander, und ein
 * Platzhalter, den der Server nicht kennt, wuerde beim Speichern abgewiesen.
 *
 * **Die Vorschau fuellt mit Beispielwerten**, nicht mit einem echten Ticket:
 * Die Maske soll zeigen, wie die Vorlage aussieht, und keine Kundendaten in
 * die Verwaltung holen.
 */
export function TemplatesPanel() {
  const { t } = useTranslation();
  const events = useMailEvents();
  const templates = useEmailTemplates();
  const [editing, setEditing] = useState<string | null>(null);

  const list = templates.data ?? [];
  const current =
    editing && editing !== 'new' ? list.find((item) => item.id === editing) : undefined;

  return (
    <div className="stack">
      <h2>{t('mail.templates')}</h2>

      <EventsSection events={events.data ?? []} templates={list} pending={events.isPending} />
      {events.isError ? <ErrorNotice error={events.error} /> : null}

      <TemplatesSection
        templates={list}
        pending={templates.isPending}
        onEdit={setEditing}
        editing={editing}
      />
      {templates.isError ? <ErrorNotice error={templates.error} /> : null}

      {editing !== null && (editing === 'new' || current) ? (
        <TemplateEditor
          key={editing}
          template={current}
          events={events.data ?? []}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : null}

      <p className="table__muted">{t('mailTemplates.contextHint')}</p>
    </div>
  );
}

function EventsSection({
  events,
  templates,
  pending,
}: {
  events: MailEvent[];
  templates: EmailTemplate[];
  pending: boolean;
}) {
  const { t } = useTranslation();
  const assign = useAssignMailEvent();

  return (
    <section className="card">
      <div className="chart-card__head">
        <h2>{t('mailTemplates.eventsTitle')}</h2>
        <p className="chart-card__hint">{t('mailTemplates.eventsHint')}</p>
      </div>
      {pending ? <SkeletonTable columns={3} /> : null}
      {assign.isError ? <ErrorNotice error={assign.error} /> : null}
      {events.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('mailTemplates.event')}</th>
                <th scope="col">{t('mailTemplates.audience')}</th>
                <th scope="col">{t('mailTemplates.template')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.key}>
                  <td>
                    <span className="table__subject">{t(`mailEvent.${event.key}.name`)}</span>
                    <br />
                    <span className="table__muted">{t(`mailEvent.${event.key}.hint`)}</span>
                  </td>
                  <td>
                    <Badge tone={event.audience === 'customer' ? 'info' : 'neutral'}>
                      {t(`mailAudience.${event.audience}`)}
                    </Badge>
                  </td>
                  <td style={{ minWidth: '16rem' }}>
                    <Select
                      aria-label={`${t('mailTemplates.template')}: ${t(`mailEvent.${event.key}.name`)}`}
                      value={event.template_id ?? ''}
                      disabled={assign.isPending}
                      onChange={(change) => {
                        assign.mutate({
                          eventKey: event.key,
                          templateId: change.target.value === '' ? null : change.target.value,
                        });
                      }}
                    >
                      {event.can_disable ? (
                        <option value="">{t('mailTemplates.doNotSend')}</option>
                      ) : null}
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function TemplatesSection({
  templates,
  pending,
  editing,
  onEdit,
}: {
  templates: EmailTemplate[];
  pending: boolean;
  editing: string | null;
  onEdit: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteEmailTemplate();
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <section className="card">
      <div className="chart-card__head">
        <div className="page-header__title">
          <h2>{t('mailTemplates.templatesTitle')}</h2>
          <Button
            variant="primary"
            disabled={editing !== null}
            onClick={() => {
              onEdit('new');
            }}
          >
            {t('mailTemplates.new')}
          </Button>
        </div>
        <p className="chart-card__hint">{t('mailTemplates.templatesHint')}</p>
      </div>
      {pending ? <SkeletonTable columns={3} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {templates.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('mailTemplates.name')}</th>
                <th scope="col">{t('mailTemplates.usedBy')}</th>
                <th scope="col">{t('tickets.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td className="table__subject">{template.name}</td>
                  <td>
                    {template.events.length === 0 ? (
                      <span className="table__muted">{t('mailTemplates.unused')}</span>
                    ) : (
                      template.events.map((key) => (
                        <Badge key={key} tone="neutral">
                          {t(`mailEvent.${key}.name`)}
                        </Badge>
                      ))
                    )}
                  </td>
                  <td>
                    <span className="row">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          onEdit(template.id);
                        }}
                      >
                        {t('mailTemplates.edit')}
                      </Button>
                      {confirming === template.id ? (
                        <>
                          <Button
                            variant="danger"
                            busy={remove.isPending}
                            onClick={() => {
                              remove.mutate(template.id, {
                                onSettled: () => {
                                  setConfirming(null);
                                },
                              });
                            }}
                          >
                            {t('mailTemplates.confirmDeleteYes')}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setConfirming(null);
                            }}
                          >
                            {t('app.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.reset();
                            setConfirming(template.id);
                          }}
                        >
                          {t('mailTemplates.delete')}
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function TemplateEditor({
  template,
  events,
  onDone,
}: {
  template: EmailTemplate | undefined;
  events: MailEvent[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateEmailTemplate();
  const update = useUpdateEmailTemplate();
  const preview = usePreviewEmailTemplate();

  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body_html ?? '');
  // Fuer welchen Anlass Variablen und Vorschau gelten: der erste zugewiesene,
  // sonst der erste ueberhaupt.
  const [context, setContext] = useState(template?.events[0] ?? events[0]?.key ?? '');

  const insertIntoBody = useRef<((text: string) => void) | null>(null);
  const subjectField = useRef<HTMLInputElement>(null);
  // Wo zuletzt geschrieben wurde -- dorthin geht ein angeklickter Platzhalter.
  const [target, setTarget] = useState<'subject' | 'body'>('body');

  const event = events.find((item) => item.key === context);
  const groups = [...new Set((event?.variables ?? []).map((variable) => variable.group))];
  const mutation = template ? update : create;

  const insert = (name: string) => {
    const placeholder = `{{${name}}}`;
    if (target === 'body' && insertIntoBody.current) {
      insertIntoBody.current(placeholder);
      return;
    }
    const field = subjectField.current;
    const start = field?.selectionStart ?? subject.length;
    const end = field?.selectionEnd ?? subject.length;
    setSubject(subject.slice(0, start) + placeholder + subject.slice(end));
  };

  const save = () => {
    const payload = { name: name.trim(), subject, body_html: body };
    if (template) {
      update.mutate({ templateId: template.id, payload }, { onSuccess: onDone });
    } else {
      create.mutate(payload, { onSuccess: onDone });
    }
  };

  return (
    <section className="card">
      <div className="chart-card__head">
        <h2>{template ? t('mailTemplates.editTitle') : t('mailTemplates.newTitle')}</h2>
      </div>

      <div className="template-editor">
        <div className="stack">
          <div className="field">
            <label className="field__label" htmlFor="template-name">
              {t('mailTemplates.name')}
            </label>
            <input
              id="template-name"
              className="field__control"
              value={name}
              maxLength={200}
              onChange={(change) => {
                setName(change.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="template-subject">
              {t('mailTemplates.subject')}
            </label>
            <input
              id="template-subject"
              ref={subjectField}
              className="field__control"
              value={subject}
              maxLength={500}
              onFocus={() => {
                setTarget('subject');
              }}
              onChange={(change) => {
                setSubject(change.target.value);
              }}
            />
          </div>

          <div
            className="field"
            onFocus={() => {
              setTarget('body');
            }}
          >
            <label className="field__label" htmlFor="template-body">
              {t('mailTemplates.body')}
            </label>
            <RichTextEditor
              id="template-body"
              ariaLabel={t('mailTemplates.body')}
              value={body}
              onChange={setBody}
              insertRef={insertIntoBody}
              features={{ image: false }}
              minRows={12}
            />
          </div>

          {mutation.isError ? <ErrorNotice error={mutation.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              busy={mutation.isPending}
              disabled={!name.trim() || !subject.trim()}
              onClick={save}
            >
              {t('app.save')}
            </Button>
            <Button
              variant="secondary"
              busy={preview.isPending}
              disabled={!context}
              onClick={() => {
                preview.mutate({ event_key: context, subject, body_html: body });
              }}
            >
              {t('mailTemplates.preview')}
            </Button>
            <Button variant="ghost" onClick={onDone}>
              {t('app.cancel')}
            </Button>
          </div>
        </div>

        <aside className="template-editor__variables" aria-label={t('mailTemplates.variables')}>
          <div className="field">
            <label className="field__label" htmlFor="template-context">
              {t('mailTemplates.context')}
            </label>
            <Select
              id="template-context"
              value={context}
              onChange={(change) => {
                setContext(change.target.value);
                preview.reset();
              }}
            >
              {events.map((item) => (
                <option key={item.key} value={item.key}>
                  {t(`mailEvent.${item.key}.name`)}
                </option>
              ))}
            </Select>
          </div>
          <p className="field__hint">{t('mailTemplates.variablesHint')}</p>
          {groups.map((group) => (
            <div key={group} className="template-editor__group">
              <h3>{t(`mailVariableGroup.${group}`)}</h3>
              {(event?.variables ?? [])
                .filter((variable) => variable.group === group)
                .map((variable) => (
                  <button
                    key={variable.name}
                    type="button"
                    className="template-editor__variable"
                    title={t(`mailVariable.${variable.name}`)}
                    onMouseDown={(press) => {
                      // Den Fokus nicht vom Feld nehmen -- sonst ginge die
                      // Schreibmarke verloren, an der eingefuegt werden soll.
                      press.preventDefault();
                    }}
                    onClick={() => {
                      insert(variable.name);
                    }}
                  >
                    <code>{`{{${variable.name}}}`}</code>
                    <span>{t(`mailVariable.${variable.name}`)}</span>
                  </button>
                ))}
            </div>
          ))}
        </aside>
      </div>

      {preview.isError ? <ErrorNotice error={preview.error} /> : null}
      {preview.data ? (
        <div className="template-editor__preview" aria-live="polite">
          <h3>{t('mailTemplates.preview')}</h3>
          <p className="field__hint">{t('mailTemplates.previewHint')}</p>
          {preview.data.unknown_variables.length > 0 ? (
            <div className="notice notice--warning" role="alert">
              {t('mailTemplates.unknownVariables', {
                names: preview.data.unknown_variables.join(', '),
              })}
            </div>
          ) : null}
          <p>
            <strong>{preview.data.subject}</strong>
          </p>
          <RichText html={preview.data.body_html} />
        </div>
      ) : null}
    </section>
  );
}
